import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { ConflictResolution, RosterPlan } from '../types';

/**
 * Spisak vozača naspram ERP šifarnika.
 *
 * Šifarnik je izvor istine, ali izvor koji se ne sme čitati naivno: kad neko
 * ode iz firme, njegov red preuzme sledeći zaposleni, a firmin telefon pređe na
 * novog čoveka. Zbog toga ovde stoje dva odvojena spiska — ono što aplikacija
 * radi sama, i ono na šta odgovara čovek.
 *
 * Sukob se ne rešava pogađanjem. Ista tri podatka — drugo ime na istom redu —
 * znače tri različita događaja: čovek je otišao, čovek je promenio prezime, ili
 * mu je neko uzeo red a on i dalje vozi. Zato tri dugmeta, ne jedno.
 */
export default function DriverRoster() {
  const [plan, setPlan] = useState<RosterPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ishod, setIshod] = useState<string | null>(null);

  // Gašenja koja su preskočena jer čovek vozi. Stoje odvojeno od ishoda, jer
  // nisu izveštaj o uspehu nego posao koji je ostao.
  const [odlozeno, setOdlozeno] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      setPlan(await api.rosterPlan());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const primeni = async () => {
    setBusy(true);
    try {
      const r = await api.applyRoster();
      setIshod(
        `Dodato ${r.dodato}, osveženo ${r.osvezeno}, ugašeno ${r.ugaseno}.`
        + (r.cekaCoveka > 0 ? ` Još ${r.cekaCoveka} čeka tebe.` : '')
      );
      setOdlozeno(r.odlozeno ?? []);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const razresi = async (driverId: number, odluka: ConflictResolution) => {
    setBusy(true);
    try {
      await api.resolveRoster(driverId, odluka);
      setIshod(null);
      setOdlozeno([]);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <section className="panel">
        <h2>Vozači i šifarnik</h2>
        <div className="health health--bad">{error}</div>
      </section>
    );
  }

  if (!plan) {
    return null;
  }

  if (plan.isQuiet) {
    return (
      <section className="panel">
        <h2>Vozači i šifarnik</h2>
        <p className="empty">
          <strong>Spisak je u koraku sa šifarnikom.</strong><br />
          Svi vozači su u ERP šifarniku, brojevi se poklapaju, i nijedan broj ne
          nose dva čoveka.
        </p>
      </section>
    );
  }

  return (
    <section className="panel panel--todo">
      <h2>Vozači i šifarnik</h2>

      {ishod && <p className="note">{ishod}</p>}

      {odlozeno.length > 0 && (
        <div className="health health--bad">
          <strong>Ovi nisu ugašeni iako ih šifarnik gasi — voze.</strong>
          <br />
          Gašenje bi im link prekinulo usred razvoza. Odbaci turu ako nije
          krenula, pa je složi na drugog vozača — ili je zatvori kad se zna šta
          je s robom. Onda primeni ponovo.
          <ul className="rows">
            {odlozeno.map((o) => (
              <li key={o}>
                <span className="row__name">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {plan.safeCount > 0 && (
        <>
          <p className="note">
            Ovo je jasno i ide bez pitanja — nov vozač, promenjen broj, ili čovek
            koji je u šifarniku <strong>izričito ugašen</strong>.
          </p>

          <ul className="rows">
            {plan.additions.map((a) => (
              <li key={`nov-${a.externalId}`}>
                <span className="row__name">{a.fullName}</span>
                <span className="row__meta">nov vozač u šifarniku · {a.phone ?? 'bez broja'}</span>
              </li>
            ))}

            {plan.updates.map((u) => (
              <li key={`izmena-${u.driverId}`}>
                <span className="row__name">{u.fullName}</span>
                <span className="row__meta">
                  {u.phoneChanged && <>broj: {u.oldPhone || 'prazno'} → {u.newPhone || 'prazno'}</>}
                  {u.phoneChanged && (u.movedRow || u.fullName !== u.newName) && ' · '}
                  {u.fullName !== u.newName && <>ime: {u.fullName} → {u.newName}</>}
                  {u.movedRow && ' · prešao na drugi red u šifarniku'}
                </span>
              </li>
            ))}

            {plan.retirements.map((r) => (
              <li key={`gasi-${r.driverId}`}>
                <span className="row__name">{r.fullName}</span>
                <span className="row__meta">{r.reason} Gasi se, broj mu se oslobađa.</span>
              </li>
            ))}
          </ul>

          <p className="disc__action">
            <button type="button" className="primary" disabled={busy} onClick={() => void primeni()}>
              {busy ? 'Primenjujem…' : `Primeni (${plan.safeCount})`}
            </button>
          </p>
        </>
      )}

      {plan.conflicts.length > 0 && (
        <>
          <h3>Ovo aplikacija ne sme sama</h3>
          <p className="note">
            Red u šifarniku sad drži drugo ime. To ume da znači tri različite
            stvari, a u podacima izgledaju isto — zato pitamo tebe.
          </p>

          <ul className="rows">
            {plan.conflicts.map((c) => (
              <li key={`sukob-${c.driverId}`}>
                <span className="row__name">{c.question}</span>
                <span className="row__meta">
                  novi broj: {c.theirPhone ?? 'nema'}
                </span>
                <span className="row__meta">
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void razresi(c.driverId, 'SamePerson')}
                  >
                    isti čovek, samo drugačije upisan
                  </button>
                  {' · '}
                  <button
                    type="button"
                    className="link link--danger"
                    disabled={busy}
                    onClick={() => void razresi(c.driverId, 'OldOneLeft')}
                  >
                    {c.ourName} je otišao iz firme
                  </button>
                  {' · '}
                  <button
                    type="button"
                    className="link"
                    disabled={busy}
                    onClick={() => void razresi(c.driverId, 'OldOneStays')}
                  >
                    {c.ourName} i dalje vozi — uzeli su mu red
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {plan.unbacked.length > 0 && (
        <>
          <h3>Voze, a u šifarniku ih nema</h3>
          <p className="note">
            Ne gase se — dok se redovi u šifarniku prepisuju, nestanak iz spiska
            ne znači odlazak iz firme. Ali dok ih tamo neko ne unese, aplikacija
            im ne može proveriti broj.
          </p>

          <ul className="rows">
            {plan.unbacked.map((u) => (
              <li key={`bez-${u.driverId}`}>
                <span className="row__name">{u.fullName}</span>
                <span className="row__meta">{u.reason} · {u.phone ?? 'bez broja'}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {plan.sharedPhones.length > 0 && (
        <>
          <h3>Isti broj na dva vozača</h3>
          <p className="note">
            Link ture ide na broj. Dok ga nose dvojica, ne zna se ko će ga
            otvoriti — a u njemu su tuđe adrese kupaca.
          </p>

          <ul className="rows">
            {plan.sharedPhones.map((s) => (
              <li key={`broj-${s.phone}`}>
                <span className="row__name">{s.phone}</span>
                <span className="row__meta">{s.names.join(' i ')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
